import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  BarChart3,
  Download,
  Filter,
  TrendingUp,
  Calendar,
  Target,
  Users,
  Edit3,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Save,
  AlertTriangle,
  Lightbulb
} from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../config/api';
import { useNotification } from '../contexts/NotificationContext';
import { useSubscription } from '../hooks/useSubscription';
import { FilterBuilder } from '../components/FilterBuilder';
import axios from 'axios';

export default function SavedReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { hasFeature, isLoading: subscriptionLoading } = useSubscription();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddResults, setShowAddResults] = useState(false);
  const [availableResults, setAvailableResults] = useState([]);
  const [selectedResults, setSelectedResults] = useState([]);
  const [viewMode, setViewMode] = useState('chart'); // 'table' or 'chart'
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Edit mode state
  const isEditMode = location.pathname.includes('/edit');
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    report_type: '',
    filters: {},
    chart_config: {},
    auto_update: false,
    date_range: {
      start_date: '',
      end_date: ''
    }
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingResultId, setDeletingResultId] = useState(null);

  // Stable callback for updating filters
  const updateFilters = useCallback((newFilters) => {
    setEditFormData(prev => ({ ...prev, filters: newFilters }));
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`/api/saved-reports/${id}`), {
        headers: getAuthHeaders()
      });
      const reportData = response.data;
      setReport(reportData);

      // Populate edit form data
      setEditFormData({
        name: reportData.name || '',
        description: reportData.description || '',
        report_type: reportData.report_type || '',
        filters: reportData.filters || {},
        chart_config: reportData.chart_config || {},
        auto_update: reportData.auto_update || false,
        date_range: reportData.date_range || {
          start_date: '',
          end_date: ''
        }
      });
    } catch (error) {
      console.error('Error fetching report:', error);
      showNotification('Failed to fetch report', 'error');
      navigate('/insights');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showNotification]);

  useEffect(() => {

    // Only proceed if subscription data has finished loading
    if (id && !subscriptionLoading && hasFeature('insights')) {
      fetchReport();
    } else if (id && !subscriptionLoading && !hasFeature('insights')) {
      console.log('User does not have insights feature access');
    } else if (id && subscriptionLoading) {
      console.log('Subscription still loading, waiting...');
    }
  }, [id, subscriptionLoading, fetchReport]);

  const fetchAvailableResults = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/api/results'), {
        headers: getAuthHeaders()
      });
      
      // Filter out results already in this report
      const reportResultIds = new Set(report.results.map(r => r.id));
      const notInReport = response.data.filter(result => !reportResultIds.has(result.id));
      
      // Apply the report's filter criteria to only show matching results
      const filtered = applyReportFilters(notInReport);
      
      setAvailableResults(filtered);
    } catch (error) {
      console.error('Error fetching available results:', error);
      showNotification('Failed to fetch available results', 'error');
    }
  }, [report, showNotification]);

  // Filter events within a result for display purposes
  const filterEventsForDisplay = useCallback((result) => {
    // Use edit form data for filters if in edit mode, otherwise use report filters
    const filters = isEditMode ? editFormData.filters : report.filters;
    
    if (!filters || Object.keys(filters).length === 0) {
      return result; // No filters set, return result as-is
    }

    const resultData = result.result_data;
    
    if (!resultData?.events || !Array.isArray(resultData.events)) {
      return result; // No events to filter
    }

    // Helper function to evaluate filter conditions (same as applyReportFilters)
    const evaluateFilter = (filterConfig, getValue) => {
      if (!filterConfig) return true;

      // Handle legacy string format for backward compatibility
      if (typeof filterConfig === 'string') {
        return getValue().toLowerCase().includes(filterConfig.toLowerCase());
      }

      // Handle new object format with logic and groups
      if (filterConfig.logic && (filterConfig.conditions || filterConfig.groups)) {
        const evaluateCondition = (condition) => {
          const value = getValue().toLowerCase();
          switch (condition.type) {
            case 'contains':
              return value.includes(condition.value.toLowerCase());
            case 'startsWith':
              return value.startsWith(condition.value.toLowerCase());
            case 'endsWith':
              return value.endsWith(condition.value.toLowerCase());
            case 'exact':
              return value === condition.value.toLowerCase();
            case 'regex':
              try {
                const regex = new RegExp(condition.value, 'i');
                return regex.test(value);
              } catch (e) {
                return false;
              }
            default:
              return false;
          }
        };

        const evaluateGroup = (group) => {
          if (!group.conditions) return true;
          const conditionResults = group.conditions.map(evaluateCondition);
          const groupResults = group.groups ? group.groups.map(evaluateGroup) : [];
          const allResults = [...conditionResults, ...groupResults];
          const connectors = group.connectors || [];

          if (allResults.length === 0) return true;
          if (allResults.length === 1) return allResults[0];

          let result = allResults[0];
          for (let i = 1; i < allResults.length; i++) {
            const connector = connectors[i - 1] || { type: 'AND' };
            const nextResult = allResults[i];
            
            switch (connector.type) {
              case 'AND':
                result = result && nextResult;
                break;
              case 'OR':
                result = result || nextResult;
                break;
              case 'NOT':
                result = result && !nextResult;
                break;
              default:
                result = result && nextResult;
            }
          }
          return result;
        };

        const conditionResults = filterConfig.conditions ? filterConfig.conditions.map(evaluateCondition) : [];
        const groupResults = filterConfig.groups ? filterConfig.groups.map(evaluateGroup) : [];
        const allResults = [...conditionResults, ...groupResults];
        const connectors = filterConfig.connectors || [];

        if (allResults.length === 0) return true;
        if (allResults.length === 1) return allResults[0];

        let result = allResults[0];
        for (let i = 1; i < allResults.length; i++) {
          const connector = connectors[i - 1] || { type: 'AND' };
          const nextResult = allResults[i];
          
          switch (connector.type) {
            case 'AND':
              result = result && nextResult;
              break;
            case 'OR':
              result = result || nextResult;
              break;
            case 'NOT':
              result = result && !nextResult;
              break;
            default:
              result = result && nextResult;
          }
        }
        return result;
      }
      return true;
    };

    // Filter events based on the report's filter criteria
    const filteredEvents = resultData.events.map(event => {
      let eventMatches = true;

      // Event name filter
      if (filters.eventName) {
        const eventNameMatch = evaluateFilter(filters.eventName, () => event.name || '');
        if (!eventNameMatch) eventMatches = false;
      }

      // Contestant name filter
      if (filters.contestantName) {
        const contestantMatch = event.draws?.some(draw =>
          draw.contestants?.some(contestant =>
            evaluateFilter(filters.contestantName, () => contestant.contestantName || '')
          )
        );
        if (!contestantMatch) eventMatches = false;
      }

      // Stock name filter
      if (filters.stockName) {
        const stockMatch = event.draws?.some(draw =>
          draw.contestants?.some(contestant =>
            evaluateFilter(filters.stockName, () => contestant.stockName || '')
          )
        );
        if (!stockMatch) eventMatches = false;
      }

      // Position filter
      if (filters.position) {
        const positionMatch = event.draws?.some(draw =>
          draw.contestants?.some(contestant =>
            evaluateFilter(filters.position, () => contestant.position || '')
          )
        );
        if (!positionMatch) eventMatches = false;
      }

      // Time Status filter - filter draws within the event
      let filteredDraws = event.draws || [];
      if (filters.timeStatus && event.draws && Array.isArray(event.draws)) {
        filteredDraws = event.draws.filter(draw => {
          const timeStatus = draw.timeStatus || '';
          return evaluateFilter(filters.timeStatus, () => timeStatus);
        });
      }

      // If event doesn't match other criteria, return null to exclude it
      if (!eventMatches) {
        return null;
      }

      // If no draws match the time status filter, return null to exclude the event
      if (filters.timeStatus && filteredDraws.length === 0) {
        return null;
      }

      // Return the event with filtered draws
      return {
        ...event,
        draws: filteredDraws
      };
    }).filter(event => event !== null); // Remove null events

    // Return a copy of the result with only the filtered events
    return {
      ...result,
      result_data: {
        ...resultData,
        events: filteredEvents
      }
    };
  }, [report, isEditMode, editFormData.filters]);

  const handleAddResults = async () => {
    if (selectedResults.length === 0) {
      showNotification('Please select at least one result to add', 'error');
      return;
    }

    try {
      await axios.post(getApiUrl(`/api/saved-reports/${id}/results`), {
        result_ids: selectedResults
      }, {
        headers: getAuthHeaders()
      });

      showNotification('Results added successfully', 'success');
      setShowAddResults(false);
      setSelectedResults([]);
      fetchReport(); // Refresh the report
    } catch (error) {
      console.error('Error adding results:', error);
      showNotification('Failed to add results', 'error');
    }
  };

  const handleRemoveResult = async (resultId) => {
    setDeletingResultId(resultId);
    setShowDeleteConfirm(true);
  };

  const confirmRemoveResult = async () => {
    if (!deletingResultId) return;

    try {
      await axios.delete(getApiUrl(`/api/saved-reports/${id}/results`), {
        headers: getAuthHeaders(),
        data: { result_ids: [deletingResultId] }
      });

      showNotification('Result removed successfully', 'success');
      fetchReport(); // Refresh the report
    } catch (error) {
      console.error('Error removing result:', error);
      showNotification('Failed to remove result', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setDeletingResultId(null);
    }
  };

  const handleDeleteReport = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteReport = async () => {
    try {
      await axios.delete(getApiUrl(`/api/saved-reports/${id}`), {
        headers: getAuthHeaders()
      });

      showNotification('Report deleted successfully', 'success');
      navigate('/insights');
    } catch (error) {
      console.error('Error deleting report:', error);
      showNotification('Failed to delete report', 'error');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleExportReport = () => {
    if (!report || !report.results || report.results.length === 0) {
      showNotification('No data to export', 'error');
      return;
    }

    try {
      // Create CSV content
      const headers = ['Show Name', 'Show Date', 'Event', 'Draw Number', 'Final Time', 'Horse Name', 'Contestant Name', 'Rank', 'Earnings'];
      const csvContent = [
        headers.join(','),
        ...report.results.flatMap(result => 
          result.result_data?.events?.flatMap(event => 
            event.draws?.map(draw => [
              `"${result.show_name || ''}"`,
              `"${result.show_start_date || ''}"`,
              `"${event.name || ''}"`,
              `"${draw.drawNumber || ''}"`,
              `"${draw.finalTime || ''}"`,
              `"${draw.contestants?.[0]?.stockName || ''}"`,
              `"${draw.contestants?.[0]?.contestantName || ''}"`,
              `"${draw.rank1 || ''}"`,
              `"${draw.contestants?.[0]?.earnings || ''}"`
            ].join(',')) || []
          ) || []
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${report.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_report.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting report:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  // Apply the report's filters to determine which results to show
  const applyReportFilters = useCallback((results) => {
    if (!report || !report.filters || Object.keys(report.filters).length === 0) {
      return results; // No filters set, show all results
    }

    // Use edit form data for date range if in edit mode
    const dateRange = isEditMode ? editFormData.date_range : report.date_range;
    

    return results.filter(result => {
      const resultData = result.result_data;
      // Use edit form data for filters if in edit mode, otherwise use report filters
      const filters = isEditMode ? editFormData.filters : report.filters;
      let matches = true;

      // Helper function to evaluate filter conditions with nested groups
      const evaluateFilter = (filterConfig, getValue) => {
        if (!filterConfig) return true;

        // Handle legacy string format for backward compatibility
        if (typeof filterConfig === 'string') {
          return getValue().toLowerCase().includes(filterConfig.toLowerCase());
        }

        // Handle new object format with logic and groups
        if (filterConfig.logic && (filterConfig.conditions || filterConfig.groups)) {
          const evaluateCondition = (condition) => {
            const value = getValue().toLowerCase();
            switch (condition.type) {
              case 'contains':
                return value.includes(condition.value.toLowerCase());
              case 'startsWith':
                return value.startsWith(condition.value.toLowerCase());
              case 'endsWith':
                return value.endsWith(condition.value.toLowerCase());
              case 'exact':
                return value === condition.value.toLowerCase();
              case 'regex':
                try {
                  const regex = new RegExp(condition.value, 'i');
                  return regex.test(value);
                } catch (e) {
                  return false;
                }
              default:
                return false;
            }
          };

          const evaluateGroup = (group) => {
            const groupResults = (group.conditions || []).map(evaluateCondition);
            switch (group.logic) {
              case 'AND':
                return groupResults.every(result => result === true);
              case 'OR':
                return groupResults.some(result => result === true);
              case 'NOT':
                return !groupResults.some(result => result === true);
              default:
                return true;
            }
          };

          // Evaluate root level conditions
          const conditionResults = filterConfig.conditions ? 
            filterConfig.conditions.map(evaluateCondition) : [];
          
          // Evaluate groups
          const groupResults = filterConfig.groups ? 
            filterConfig.groups.map(evaluateGroup) : [];

          // Combine all results with connectors
          const allResults = [...conditionResults, ...groupResults];
          const connectors = filterConfig.connectors || [];

          if (allResults.length === 0) return true;
          if (allResults.length === 1) return allResults[0];

          // Apply connectors between results
          let result = allResults[0];
          for (let i = 1; i < allResults.length; i++) {
            const connector = connectors[i - 1] || { type: 'AND' };
            const nextResult = allResults[i];
            
            switch (connector.type) {
              case 'AND':
                result = result && nextResult;
                break;
              case 'OR':
                result = result || nextResult;
                break;
              case 'NOT':
                result = result && !nextResult;
                break;
              default:
                result = result && nextResult;
            }
          }

          return result;
        }

        return true;
      };
      
      // Show name filter
      if (filters.showName) {
        const showName = resultData?.ShowName || '';
        const showNameMatch = evaluateFilter(filters.showName, () => showName);
        if (!showNameMatch) matches = false;
      }

      // Event name filter
      if (filters.eventName) {
        const eventNameMatch = resultData?.events?.some(event =>
          evaluateFilter(filters.eventName, () => event.name || '')
        );
        if (!eventNameMatch) matches = false;
      }

      // Contestant name filter
      if (filters.contestantName) {
        const contestantMatch = resultData?.events?.some(event => {
          if (event.draws) {
            return event.draws.some(draw =>
              draw.contestants?.some(contestant =>
                evaluateFilter(filters.contestantName, () => contestant.contestantName || '')
              )
            );
          }
          return false;
        });
        if (!contestantMatch) matches = false;
      }

      // Stock name filter
      if (filters.stockName) {
        const stockMatch = resultData?.events?.some(event => {
          if (event.draws) {
            return event.draws.some(draw =>
              draw.contestants?.some(contestant =>
                evaluateFilter(filters.stockName, () => contestant.stockName || '')
              )
            );
          }
          return false;
        });
        if (!stockMatch) matches = false;
      }

      // Position filter
      if (filters.position) {
        const positionMatch = resultData?.events?.some(event => {
          if (event.draws) {
            return event.draws.some(draw =>
              draw.contestants?.some(contestant =>
                evaluateFilter(filters.position, () => contestant.position || '')
              )
            );
          }
          return false;
        });
        if (!positionMatch) matches = false;
      }

      // Time Status filter is now handled at the draw level in filterEventsForDisplay

      // Date range filters (these remain simple string comparisons for now)
      if (filters.from) {
        const resultDate = new Date(result.created_at);
        const fromDate = new Date(filters.from);
        if (resultDate < fromDate) matches = false;
      }

      if (filters.to) {
        const resultDate = new Date(result.created_at);
        const toDate = new Date(filters.to);
        if (resultDate > toDate) matches = false;
      }

      // New date range filter using show_start_date
      if (dateRange?.start_date) {
        const resultDate = new Date(result.show_start_date || result.created_at);
        const startDate = new Date(dateRange.start_date);
        if (resultDate < startDate) matches = false;
      }

      if (dateRange?.end_date) {
        const resultDate = new Date(result.show_start_date || result.created_at);
        const endDate = new Date(dateRange.end_date);
        if (resultDate > endDate) matches = false;
      }

      return matches;
    });
  }, [report, isEditMode, editFormData.date_range]);

  const getEventContestants = (event) => {
    if (!event || !event.draws) return null;
    
    const contestants = [];
    event.draws.forEach(draw => {
      if (draw.contestants) {
        draw.contestants.forEach(contestant => {
          if (contestant.contestantName && !contestants.includes(contestant.contestantName)) {
            contestants.push(contestant.contestantName);
          }
        });
      }
    });
    
    return contestants.length > 0 ? contestants.join(', ') : null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${month}/${day}/${year}`;
  };

  const exportToCSV = () => {
    if (!report || !report.results) return;

    // Apply both result-level and event-level filtering (same as display)
    const results = applyReportFilters(report.results);
    const filteredResults = results.map(result => filterEventsForDisplay(result));
    const csvRows = [];
    
    // Headers
    csvRows.push([
      'Show Date',
      'Show Name',
      'Contestant',
      'Event Name',
      'Final Time',
      'Position',
      'Earnings',
      'Points'
    ]);
    
    // Data rows
    filteredResults.forEach(result => {
      if (result.result_data?.events) {
        result.result_data.events.forEach(event => {
          if (event.draws && Array.isArray(event.draws)) {
            event.draws.forEach(draw => {
              if (draw.finalTime) {
                csvRows.push([
                  result.show_start_date || result.created_at || '',
                  result.show_name || '',
                  getEventContestants(event) || '',
                  event.name || '',
                  draw.finalTime || '',
                  draw.rank1 || draw.rank2 || '',
                  draw.earnings || '',
                  draw.points || ''
                ]);
              }
            });
          }
        });
      }
    });
    
    // Convert to CSV
    const csvContent = csvRows.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.name}-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSaveReport = async () => {
    try {
      setSaving(true);
      const response = await axios.put(getApiUrl(`/api/saved-reports/${id}`), editFormData, {
        headers: getAuthHeaders()
      });
      
      showNotification('Report updated successfully', 'success');
      navigate(`/insights/${id}`);
    } catch (error) {
      console.error('Error updating report:', error);
      showNotification('Failed to update report', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    navigate(`/insights/${id}`);
  };

  // Render defined filters in a readable format with support for groups
  const renderDefinedFilters = () => {
    if (!report || !report.filters || Object.keys(report.filters).length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No filters defined
        </div>
      );
    }

    const renderCondition = (condition) => {
      const typeText = condition.type === 'contains' ? 'contains' :
                      condition.type === 'startsWith' ? 'starts with' :
                      condition.type === 'endsWith' ? 'ends with' :
                      condition.type === 'exact' ? 'exactly matches' :
                      condition.type === 'regex' ? 'matches regex' : 'contains';
      return `${typeText} "${condition.value}"`;
    };

    const renderGroup = (group, groupIndex) => {
      const logicText = group.logic === 'AND' ? 'AND' : 
                       group.logic === 'OR' ? 'OR' : 
                       group.logic === 'NOT' ? 'NOT' : 'AND';
      
      const conditionsText = (group.conditions || []).map(renderCondition).join(` ${logicText} `);
      
      return (
        <div key={`group-${groupIndex}`} className="ml-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
          <div className="font-medium text-blue-700 mb-1">Group ({logicText}):</div>
          <div className="text-blue-600">{conditionsText}</div>
        </div>
      );
    };

    return Object.entries(report.filters).map(([filterKey, filterConfig]) => {
      // Handle legacy string format
      if (typeof filterConfig === 'string') {
        return (
          <div key={filterKey} className="mb-4">
            <div className="text-sm font-medium text-gray-700 capitalize">
              {filterKey.replace(/([A-Z])/g, ' $1').trim()}:
            </div>
            <div className="text-sm text-gray-600 ml-2">
              contains "{filterConfig}"
            </div>
          </div>
        );
      }

      // Handle new object format with logic, groups, and connectors
      if (filterConfig.logic && (filterConfig.conditions || filterConfig.groups)) {
        const hasConditions = filterConfig.conditions && filterConfig.conditions.length > 0;
        const hasGroups = filterConfig.groups && filterConfig.groups.length > 0;
        const connectors = filterConfig.connectors || [];
        
        // Create combined list of all items
        const allItems = [
          ...(hasConditions ? filterConfig.conditions.map((condition, index) => ({ type: 'condition', data: condition, index })) : []),
          ...(hasGroups ? filterConfig.groups.map((group, index) => ({ type: 'group', data: group, index: (filterConfig.conditions?.length || 0) + index })) : [])
        ];
        
        const renderItemWithConnector = (item, index) => {
          const connector = connectors[index - 1];
          return (
            <div key={`${item.type}-${index}`}>
              {index > 0 && connector && (
                <div className="text-xs text-gray-500 font-medium my-1 text-center">
                  {connector.type}
                </div>
              )}
              {item.type === 'condition' ? 
                <div className="text-xs text-gray-600">{renderCondition(item.data)}</div> :
                renderGroup(item.data, item.index)
              }
            </div>
          );
        };
        
        return (
          <div key={filterKey} className="mb-4">
            <div className="text-sm font-medium text-gray-700 capitalize">
              {filterKey.replace(/([A-Z])/g, ' $1').trim()}:
            </div>
            <div className="text-sm text-gray-600 ml-2">
              <div className="space-y-1">
                {allItems.map(renderItemWithConnector)}
              </div>
            </div>
          </div>
        );
      }

      return null;
    });
  };

  // Compute filtered results and optimized table rows (must be before any early returns)
  const filteredResults = useMemo(() => {
    if (!report || !report.results) return [];
    const results = applyReportFilters(report.results);
    // Apply event-level filtering to each result
    return results.map(result => filterEventsForDisplay(result));
  }, [report, applyReportFilters, filterEventsForDisplay]);

  // Calculate average final time from filtered results
  const calculateAverageFinalTime = useCallback((results) => {
    const allFinalTimes = [];
    
    results.forEach(result => {
      const resultData = result.result_data;
      if (resultData?.events && Array.isArray(resultData.events)) {
        resultData.events.forEach(event => {
          if (event.draws && Array.isArray(event.draws)) {
            event.draws.forEach(draw => {
              const finalTime = draw.finalTime;
              const timeStatus = draw.timeStatus;
              
              if (finalTime && 
                  finalTime !== '' && 
                  finalTime !== 'N/A' && 
                  timeStatus !== 'N/T') {
                const timeValue = parseFloat(finalTime);
                if (!isNaN(timeValue)) {
                  allFinalTimes.push(timeValue);
                }
              }
            });
          }
        });
      }
    });
    
    return allFinalTimes.length > 0 ? allFinalTimes.reduce((a, b) => a + b, 0) / allFinalTimes.length : null;
  }, []);

  // Calculate stats from filtered results
  const filteredStats = useMemo(() => {
    if (!filteredResults || filteredResults.length === 0) {
      return {
        totalResults: 0,
        totalRuns: 0,
        uniqueShows: 0,
        avgFinalTime: null
      };
    }

    // Count total runs (draws) across all filtered results
    let totalRuns = 0;
    filteredResults.forEach(result => {
      const resultData = result.result_data;
      if (resultData?.events && Array.isArray(resultData.events)) {
        resultData.events.forEach(event => {
          if (event.draws && Array.isArray(event.draws)) {
            totalRuns += event.draws.length;
          }
        });
      }
    });

    return {
      totalResults: filteredResults.length,
      totalRuns: totalRuns,
      uniqueShows: new Set(filteredResults.map(r => r.show_name)).size,
      avgFinalTime: calculateAverageFinalTime(filteredResults)
    };
  }, [filteredResults, calculateAverageFinalTime]);

  // Optimize table rendering by pre-flattening the data structure
  const tableRows = useMemo(() => {
    return filteredResults.flatMap((result, resultIndex) =>
      result.result_data?.events?.flatMap((event, eventIndex) =>
        event.draws?.map((draw, drawIndex) => ({
          key: `${resultIndex}-${eventIndex}-${drawIndex}`,
          result,
          event,
          draw
        })) || []
      ) || []
    );
  }, [filteredResults]);

  // Early returns after all hooks
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  if (!hasFeature('insights')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Insights</h2>
          <p className="text-gray-600 mb-4">This feature is available with Showpremium subscription.</p>
          <button
            onClick={() => navigate('/subscription')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upgrade to Showpremium
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Report Not Found</h2>
          <p className="text-gray-600 mb-4">The report you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/insights')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Insights
          </button>
        </div>
      </div>
    );
  }

  // Show edit form if in edit mode
  if (isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        {/* Edit Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCancelEdit}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Cancel Edit"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit Report</h1>
                <p className="text-gray-600">Update report settings and filters</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReport}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <form className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter report name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Describe what this report tracks"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                  <select
                    value={editFormData.report_type}
                    onChange={(e) => setEditFormData({ ...editFormData, report_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="custom">Custom</option>
                    <option value="barrel_racing">Barrel Racing</option>
                    <option value="pole_bending">Pole Bending</option>
                    <option value="overview">Overview</option>
                    <option value="comparison">Comparison</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                <p className="text-sm text-gray-600">Set criteria for automatically including new results using AND, OR, NOT logic</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">How Filters Work</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>Between different filter types:</strong> All active filters must match (AND logic). 
                        For example, if you set both "Show Name" and "Event Name" filters, a result must match BOTH to be included.
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>Within each filter:</strong> You can use AND, OR, NOT logic to combine multiple conditions within the same filter type.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Show Name Filter */}
                  <FilterBuilder
                    label="Show Name"
                    filterKey="showName"
                    filters={editFormData.filters}
                    onUpdate={updateFilters}
                    placeholder="e.g., Eburg Rodeo, State Fair"
                  />
                  
                  {/* Event Name Filter */}
                  <FilterBuilder
                    label="Event Name"
                    filterKey="eventName"
                    filters={editFormData.filters}
                    onUpdate={updateFilters}
                    placeholder="e.g., Barrels, Poles, etc."
                  />
                  
                  {/* Contestant Name Filter */}
                  <FilterBuilder
                    label="Contestant Name"
                    filterKey="contestantName"
                    filters={editFormData.filters}
                    onUpdate={updateFilters}
                    placeholder="e.g., John Smith"
                  />
                  
                  {/* Stock Name Filter */}
                  <FilterBuilder
                    label="Stock Name"
                    filterKey="stockName"
                    filters={editFormData.filters}
                    onUpdate={updateFilters}
                    placeholder="e.g., horse name, bull name"
                  />
                  
                  {/* Position Filter */}
                  <FilterBuilder
                    label="Position"
                    filterKey="position"
                    filters={editFormData.filters}
                    onUpdate={updateFilters}
                    placeholder="e.g., 1, 2, 3 or first, second"
                  />
                  
                  {/* Time Status Filter */}
                  <FilterBuilder
                    label="Time Status"
                    filterKey="timeStatus"
                    filters={editFormData.filters}
                    onUpdate={updateFilters}
                    placeholder="e.g., OK, N/T, T/O, Vet"
                  />
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Date Range Filter</h3>
                <p className="text-sm text-gray-600">Filter results by show date range</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={editFormData.date_range?.start_date || ''}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        date_range: { 
                          ...editFormData.date_range, 
                          start_date: e.target.value 
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={editFormData.date_range?.end_date || ''}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        date_range: { 
                          ...editFormData.date_range, 
                          end_date: e.target.value 
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                {(editFormData.date_range?.start_date || editFormData.date_range?.end_date) && (
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setEditFormData({ 
                        ...editFormData, 
                        date_range: { start_date: '', end_date: '' }
                      })}
                      className="text-sm text-red-600 hover:text-red-800 transition-colors"
                    >
                      Clear date range
                    </button>
                  </div>
                )}
              </div>

              {/* Chart Configuration */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Chart Configuration</h3>
                <p className="text-sm text-gray-600">Configure how data will be displayed in chart view</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                    <select
                      value={editFormData.chart_config?.chartType || 'line'}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        chart_config: { 
                          ...editFormData.chart_config, 
                          chartType: e.target.value 
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="line">Line Chart</option>
                      <option value="bar">Bar Chart</option>
                      <option value="scatter">Scatter Plot</option>
                      <option value="area">Area Chart</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Y-Axis (Data)</label>
                    <select
                      value={editFormData.chart_config?.yAxis || 'time'}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        chart_config: { 
                          ...editFormData.chart_config, 
                          yAxis: e.target.value 
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="time">Final Time</option>
                      <option value="points">Points</option>
                      <option value="earnings">Earnings</option>
                      <option value="position">Position</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">X-Axis (Timeline)</label>
                    <select
                      value={editFormData.chart_config?.xAxis || 'date'}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        chart_config: { 
                          ...editFormData.chart_config, 
                          xAxis: e.target.value 
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="date">Date</option>
                      <option value="show">Show</option>
                      <option value="event">Event</option>
                      <option value="index">Sequence</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Best Time</label>
                    <select
                      value={editFormData.chart_config?.bestTimeType || 'lowest'}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        chart_config: { 
                          ...editFormData.chart_config, 
                          bestTimeType: e.target.value 
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="lowest">Lowest (Barrel Racing, Pole Bending)</option>
                      <option value="highest">Highest (Bull Riding, etc.)</option>
                    </select>
                  </div>
                  
                </div>
                
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editFormData.chart_config?.showTrendLine || false}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        chart_config: { 
                          ...editFormData.chart_config, 
                          showTrendLine: e.target.checked 
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Show trend line</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editFormData.chart_config?.showAverage || false}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        chart_config: { 
                          ...editFormData.chart_config, 
                          showAverage: e.target.checked 
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Show average line</span>
                  </label>
                </div>
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Settings</h3>
                
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editFormData.auto_update}
                      onChange={(e) => setEditFormData({ ...editFormData, auto_update: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable auto-update</span>
                  </label>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/insights')}
                className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Insights
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                  <Lightbulb className="w-8 h-8 mr-3 text-blue-600" />
                  {report.name}
                </h1>
                <p className="text-gray-600 mt-1">{report.description || 'No description'}</p>
              </div>
            </div>
            
            {!isEditMode && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setShowAddResults(true)}
                  className="flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Results
                </button>
                <button
                  onClick={() => navigate(`/insights/${id}/edit`)}
                  className="flex items-center justify-center px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit Report
                </button>
                <button
                  onClick={handleExportReport}
                  className="flex items-center justify-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4">
          <div className="flex items-center">
            <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">{filteredStats.totalRuns}</div>
              <div className="text-xs sm:text-sm text-gray-500">Total Runs</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {filteredStats.uniqueShows}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Unique Shows</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4">
          <div className="flex items-center">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {filteredStats.avgFinalTime && typeof filteredStats.avgFinalTime === 'number' 
                  ? `${Number(filteredStats.avgFinalTime).toFixed(3)}s` 
                  : 'N/A'}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Avg Time/Score</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4">
          <div className="flex items-center">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {report.auto_update ? 'Auto' : 'Manual'}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Update Mode</div>
            </div>
          </div>
        </div>
      </div>

      {/* Defined Filters Panel */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div 
          className="flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowFilterPanel(!showFilterPanel)}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Filter className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <h3 className="font-medium text-gray-800 text-sm sm:text-base">Filters</h3>
            {report && report.filters && Object.keys(report.filters).length > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex-shrink-0">
                {Object.keys(report.filters).length} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {report && report.auto_update && (
              <span className="bg-green-100 text-green-800 text-xs px-1.5 sm:px-2 py-1 rounded-full hidden sm:inline">
                Auto-update enabled
              </span>
            )}
            <div className={`transform transition-transform ${showFilterPanel ? 'rotate-180' : ''}`}>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        {showFilterPanel && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="pt-4">
              {renderDefinedFilters()}
            </div>
          </div>
        )}
      </div>

      {/* View Toggle */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4">
        <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
          <button
            onClick={() => setViewMode('chart')}
            className={`flex-1 sm:flex-none px-3 py-2 rounded text-sm font-medium transition-colors ${
              viewMode === 'chart' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="w-4 h-4 mr-1 inline" />
            Chart
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`flex-1 sm:flex-none px-3 py-2 rounded text-sm font-medium transition-colors ${
              viewMode === 'table' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Table
          </button>
        </div>
        <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-right">
          Showing {filteredStats.totalRuns} runs from {filteredStats.totalResults} of {report.results?.length || 0} shows
        </div>
      </div>

      {/* Results Table */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Show Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Show Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Contestant</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Event</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">Final Time</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-700">Position</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableRows.map((row) => (
                  <tr key={row.key} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">
                      {formatDate(row.result.show_start_date || row.result.created_at)}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {row.result.show_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-gray-900" title={getEventContestants(row.event) || 'N/A'}>
                      <span className="truncate block max-w-32">
                        {getEventContestants(row.event) || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {row.event.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">
                      {row.draw.finalTime ? `${parseFloat(row.draw.finalTime).toFixed(3)}s` : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-900">
                      {row.draw.rank1 || row.draw.rank2 || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleRemoveResult(row.result.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Remove from report"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            {tableRows.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No data available for the selected filters</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {tableRows.map((row) => (
                  <div key={row.key} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {row.event.name || 'N/A'}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {row.result.show_name || 'N/A'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveResult(row.result.id)}
                        className="ml-2 text-red-600 hover:text-red-800 transition-colors flex-shrink-0"
                        title="Remove from report"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Date:</span>
                        <p className="font-medium text-gray-900">
                          {formatDate(row.result.show_start_date || row.result.created_at)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Time:</span>
                        <p className="font-medium text-gray-900">
                          {row.draw.finalTime ? `${parseFloat(row.draw.finalTime).toFixed(3)}s` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Contestant:</span>
                        <p className="font-medium text-gray-900 truncate" title={getEventContestants(row.event) || 'N/A'}>
                          {getEventContestants(row.event) || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Position:</span>
                        <p className="font-medium text-gray-900">
                          {row.draw.rank1 || row.draw.rank2 || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chart View */}
      {viewMode === 'chart' && (
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
            Performance Chart
          </h3>
          
          {filteredStats.totalRuns === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No data available for the selected filters</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Dynamic Chart based on chart_config */}
              <div>
                <h4 className="text-sm sm:text-md font-medium text-gray-700 mb-2 sm:mb-3">
                  <span className="block sm:inline">
                    {report.chart_config?.chartType === 'bar' ? 'Bar Chart' : 
                     report.chart_config?.chartType === 'scatter' ? 'Scatter Plot' :
                     report.chart_config?.chartType === 'area' ? 'Area Chart' : 
                     'Line Chart'} - Final Times Over Time
                  </span>
                  {report.chart_config?.bestTimeType === 'highest' && (
                    <span className="block sm:inline text-xs sm:text-sm text-orange-600 sm:ml-2 mt-1 sm:mt-0">(Higher is Better)</span>
                  )}
                </h4>
                <div className="h-48 sm:h-64 bg-gray-50 rounded-lg p-2 sm:p-4 overflow-hidden">
                  <svg className="w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="xMinYMid meet">
                    {(() => {
                      // Check if we should group by horse
                      const groupByHorse = report.chart_config?.groupByHorse || false;
                      
                      
                      let chartData;
                      let allTimes = [];
                      
                      if (groupByHorse) {
                        // Group results by horse (stock name)
                        const horseGroups = {};
                        
                        filteredResults.forEach(result => {
                          result.result_data?.events?.forEach(event => {
                            event.draws?.forEach(draw => {
                              // Get stock name from contestants array
                              const stockName = draw.contestants?.[0]?.stockName || 'Unknown Horse';
                              const time = parseFloat(draw.finalTime);
                              
                              if (!isNaN(time)) {
                                if (!horseGroups[stockName]) {
                                  horseGroups[stockName] = [];
                                }
                                horseGroups[stockName].push({
                                  time: time,
                                  date: new Date(result.show_start_date || result.created_at),
                                  show: result.show_name,
                                  event: event.name,
                                  horse: stockName
                                });
                                allTimes.push(time);
                              }
                            });
                          });
                        });
                        
                        // Sort each horse's data by date
                        Object.keys(horseGroups).forEach(horse => {
                          horseGroups[horse].sort((a, b) => a.date - b.date);
                        });
                        
                        
                        chartData = { horseGroups, isGrouped: true };
                      } else {
                        // Original single-line logic
                        const times = filteredResults
                          .flatMap(result => 
                            result.result_data?.events?.flatMap(event => 
                              event.draws?.map(draw => ({
                                time: parseFloat(draw.finalTime),
                                date: new Date(result.show_start_date || result.created_at),
                                show: result.show_name,
                                event: event.name
                              })) || []
                            ) || []
                          )
                          .filter(item => !isNaN(item.time))
                          .sort((a, b) => a.date - b.date);
                        
                        allTimes = times.map(item => item.time);
                        chartData = { times, isGrouped: false };
                      }
                      
                      if (allTimes.length === 0) return null;
                      
                      const minTime = Math.min(...allTimes);
                      const maxTime = Math.max(...allTimes);
                      const timeRange = maxTime - minTime || 1;
                      
                      // Add padding to Y-axis range (10% above and below data points)
                      const padding = timeRange * 0.1 || 0.1; // 10% padding, minimum 0.1 seconds
                      const paddedMinTime = minTime - padding;
                      const paddedMaxTime = maxTime + padding;
                      const paddedTimeRange = paddedMaxTime - paddedMinTime;
                      const chartType = report.chart_config?.chartType || 'line';
                      const showTrendLine = report.chart_config?.showTrendLine || false;
                      const showAverage = report.chart_config?.showAverage || false;
                      const bestTimeType = report.chart_config?.bestTimeType || 'lowest';
                      
                      // Helper function to calculate Y coordinate based on bestTimeType
                      const getYCoordinate = (time) => {
                        if (bestTimeType === 'highest') {
                          // For "highest is better", invert the Y-axis so higher times appear higher
                          return 40 + ((time - paddedMinTime) / paddedTimeRange) * 140;
                        } else {
                          // For "lowest is better", higher times appear lower (default behavior)
                          return 40 + ((paddedMaxTime - time) / paddedTimeRange) * 140;
                        }
                      };
                      
                      // Calculate average time
                      const averageTime = allTimes.length > 0 ? allTimes.reduce((sum, time) => sum + time, 0) / allTimes.length : 0;
                      
                      // Define colors for different horses
                      const horseColors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
                      
                      // Store values for use in legend and summary sections
                      const chartStats = {
                        allTimes,
                        averageTime,
                        minTime: allTimes.length > 0 ? Math.min(...allTimes) : 0,
                        maxTime: allTimes.length > 0 ? Math.max(...allTimes) : 0
                      };
                      
                      // Calculate trend line (simple linear regression)
                      const calculateTrendLine = () => {
                        if (allTimes.length < 2) return null;
                        
                        // Only calculate trend line for non-grouped data
                        if (chartData.isGrouped) return null;
                        
                        const times = chartData.times;
                        const n = times.length;
                        const sumX = times.reduce((sum, _, index) => sum + index, 0);
                        const sumY = times.reduce((sum, item) => sum + item.time, 0);
                        const sumXY = times.reduce((sum, item, index) => sum + (index * item.time), 0);
                        const sumXX = times.reduce((sum, _, index) => sum + (index * index), 0);
                        
                        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
                        const intercept = (sumY - slope * sumX) / n;
                        
                        return times.map((_, index) => {
                          const predictedTime = slope * index + intercept;
                          return {
                            x: times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, times.length - 1),
                            y: getYCoordinate(predictedTime)
                          };
                        });
                      };
                      
                      const trendPoints = showTrendLine ? calculateTrendLine() : null;
                      
                      return (
                        <g>
                          {/* Grid lines */}
                          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                            <line 
                              key={i} 
                              x1="50" 
                              y1={40 + ratio * 140} 
                              x2="750" 
                              y2={40 + ratio * 140} 
                              stroke="#e5e7eb" 
                              strokeWidth="1" 
                            />
                          ))}
                          
                          {/* Y-axis labels */}
                          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                            const timeValue = bestTimeType === 'highest' 
                              ? paddedMinTime + paddedTimeRange * ratio
                              : paddedMinTime + paddedTimeRange * (1 - ratio);
                            return (
                              <text 
                                key={i} 
                                x="45" 
                                y={45 + ratio * 140} 
                                textAnchor="end" 
                                className="text-xs fill-gray-600" 
                                fontSize="10"
                              >
                                {timeValue.toFixed(3)}s
                              </text>
                            );
                          })}
                          
                          {/* Average line */}
                          {showAverage && averageTime > 0 && (
                            <line
                              x1="70"
                              y1={getYCoordinate(averageTime)}
                              x2="750"
                              y2={getYCoordinate(averageTime)}
                              stroke="#ef4444"
                              strokeWidth="2"
                              strokeDasharray="5,5"
                              opacity="0.8"
                            />
                          )}
                          
                          {/* Trend line */}
                          {showTrendLine && trendPoints && trendPoints.length > 1 && (
                            <polyline
                              fill="none"
                              stroke="#10b981"
                              strokeWidth="2"
                              strokeDasharray="3,3"
                              points={trendPoints.map(point => `${point.x},${point.y}`).join(' ')}
                              opacity="0.8"
                            />
                          )}
                          
                          {/* Chart based on type */}
                          {chartData.isGrouped ? (
                            // Multi-horse line chart
                            Object.entries(chartData.horseGroups).map(([horseName, horseData], horseIndex) => {
                              const color = horseColors[horseIndex % horseColors.length];
                              const allHorseTimes = Object.values(chartData.horseGroups).flat();
                              const allDates = [...new Set(allHorseTimes.map(item => item.date.getTime()))].sort((a, b) => a - b);
                              
                              return (
                                <g key={horseName}>
                                  {/* Line for this horse */}
                                  <polyline
                                    fill="none"
                                    stroke={color}
                                    strokeWidth="2"
                                    points={horseData.map((item, index) => {
                                      const dateIndex = allDates.indexOf(item.date.getTime());
                                      const x = allDates.length === 1 ? 400 : 70 + (dateIndex * 680) / Math.max(1, allDates.length - 1);
                                      const y = getYCoordinate(item.time);
                                      return `${x},${y}`;
                                    }).join(' ')}
                                  />
                                  
                                  {/* Points for this horse */}
                                  {horseData.map((item, index) => {
                                    const dateIndex = allDates.indexOf(item.date.getTime());
                                    const x = allDates.length === 1 ? 400 : 70 + (dateIndex * 680) / Math.max(1, allDates.length - 1);
                                    const y = getYCoordinate(item.time);
                                    return (
                                      <g key={`${horseName}-${index}`}>
                                        <circle 
                                          cx={x} 
                                          cy={y} 
                                          r="3" 
                                          fill={color} 
                                          className="hover:fill-blue-600 cursor-pointer" 
                                        />
                                        <title>
                                          {horseName} - {item.show} - {item.event}: {item.time.toFixed(3)}s
                                        </title>
                                      </g>
                                    );
                                  })}
                                </g>
                              );
                            })
                          ) : chartType === 'bar' ? (
                            // Bar Chart (single line)
                            chartData.times.map((item, index) => {
                              const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                              const y = getYCoordinate(item.time);
                              const barHeight = bestTimeType === 'highest' ? y - 40 : 180 - y;
                              const barWidth = Math.max(20, 680 / chartData.times.length - 2);
                              
                              return (
                                <g key={index}>
                                  <rect 
                                    x={x - barWidth/2} 
                                    y={y} 
                                    width={barWidth} 
                                    height={barHeight} 
                                    fill="#3b82f6" 
                                    className="hover:fill-blue-600 cursor-pointer" 
                                    opacity="0.8"
                                  />
                                  <title>
                                    {item.show} - {item.event}: {item.time.toFixed(3)}s
                                  </title>
                                </g>
                              );
                            })
                          ) : chartType === 'scatter' ? (
                            // Scatter Plot (single line)
                            chartData.times.map((item, index) => {
                              const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                              const y = getYCoordinate(item.time);
                              
                              return (
                                <g key={index}>
                                  <circle 
                                    cx={x} 
                                    cy={y} 
                                    r="5" 
                                    fill="#3b82f6" 
                                    className="hover:fill-blue-600 cursor-pointer" 
                                    opacity="0.7"
                                  />
                                  <title>
                                    {item.show} - {item.event}: {item.time.toFixed(3)}s
                                  </title>
                                </g>
                              );
                            })
                          ) : chartType === 'area' ? (
                            // Area Chart (single line)
                            <g>
                              <polygon
                                fill="#3b82f6"
                                fillOpacity="0.3"
                                stroke="#3b82f6"
                                strokeWidth="2"
                                points={[
                                  `70,${bestTimeType === 'highest' ? 40 : 180}`, // Start at appropriate baseline
                                  ...chartData.times.map((item, index) => {
                                    const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                                    const y = getYCoordinate(item.time);
                                    return `${x},${y}`;
                                  }),
                                  `750,${bestTimeType === 'highest' ? 40 : 180}` // End at appropriate baseline
                                ].join(' ')}
                              />
                              <polyline
                                fill="none"
                                stroke="#3b82f6"
                                strokeWidth="2"
                                points={chartData.times.map((item, index) => {
                                  const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                                  const y = getYCoordinate(item.time);
                                  return `${x},${y}`;
                                }).join(' ')}
                              />
                              {chartData.times.map((item, index) => {
                                const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                                const y = getYCoordinate(item.time);
                                return (
                                  <g key={index}>
                                    <circle 
                                      cx={x} 
                                      cy={y} 
                                      r="3" 
                                      fill="#3b82f6" 
                                      className="hover:fill-blue-600 cursor-pointer" 
                                    />
                                    <title>
                                      {item.show} - {item.event}: {item.time.toFixed(3)}s
                                    </title>
                                  </g>
                                );
                              })}
                            </g>
                          ) : (
                            // Line Chart (default, single line)
                            <g>
                              <polyline
                                fill="none"
                                stroke="#3b82f6"
                                strokeWidth="2"
                                points={chartData.times.map((item, index) => {
                                  const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                                  const y = getYCoordinate(item.time);
                                  return `${x},${y}`;
                                }).join(' ')}
                              />
                              
                              {chartData.times.map((item, index) => {
                                const x = chartData.times.length === 1 ? 400 : 70 + (index * 680) / Math.max(1, chartData.times.length - 1);
                                const y = getYCoordinate(item.time);
                                return (
                                  <g key={index}>
                                    <circle 
                                      cx={x} 
                                      cy={y} 
                                      r="3" 
                                      fill="#3b82f6" 
                                      className="hover:fill-blue-600 cursor-pointer" 
                                    />
                                    <title>
                                      {item.show} - {item.event}: {item.time.toFixed(3)}s
                                    </title>
                                  </g>
                                );
                              })}
                            </g>
                          )}
                        </g>
                      );
                    })()}
                  </svg>
                  
                  {/* Horse Legend for grouped charts */}
                  {(() => {
                    const groupByHorse = report.chart_config?.groupByHorse || false;
                    if (groupByHorse) {
                      // Define colors for different horses (same as in chart)
                      const horseColors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
                      
                      const horseGroups = {};
                      filteredResults.forEach(result => {
                        result.result_data?.events?.forEach(event => {
                          event.draws?.forEach(draw => {
                            // Get stock name from contestants array
                            const stockName = draw.contestants?.[0]?.stockName || 'Unknown Horse';
                            if (!horseGroups[stockName]) {
                              horseGroups[stockName] = true;
                            }
                          });
                        });
                      });
                      
                      const horses = Object.keys(horseGroups);
                      if (horses.length > 0) {
                        return (
                          <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs">
                            {horses.map((horse, index) => {
                              const color = horseColors[index % horseColors.length];
                              return (
                                <div key={horse} className="flex items-center">
                                  <div 
                                    className="w-4 h-0.5 mr-2" 
                                    style={{backgroundColor: color}}
                                  ></div>
                                  <span className="text-gray-600">{horse}</span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                  
                  {/* Legend for trend and average lines */}
                  {(() => {
                    const showTrendLine = report.chart_config?.showTrendLine || false;
                    const showAverage = report.chart_config?.showAverage || false;
                    
                    // Calculate average time from filtered results
                    const allTimesForLegend = filteredResults
                      .flatMap(result => 
                        result.result_data?.events?.flatMap(event => 
                          event.draws?.map(draw => parseFloat(draw.finalTime)) || []
                        ) || []
                      )
                      .filter(time => !isNaN(time));
                    const averageTime = allTimesForLegend.length > 0 ? allTimesForLegend.reduce((sum, time) => sum + time, 0) / allTimesForLegend.length : 0;
                    
                    if (!showTrendLine && !showAverage) return null;
                    
                    return (
                      <div className="mt-3 flex justify-center space-x-6 text-xs">
                        {showAverage && (
                          <div className="flex items-center">
                            <div className="w-4 h-0.5 bg-red-500 mr-2" style={{borderTop: '2px dashed #ef4444'}}></div>
                            <span className="text-gray-600">Average: {averageTime.toFixed(3)}s</span>
                          </div>
                        )}
                        {showTrendLine && (
                          <div className="flex items-center">
                            <div className="w-4 h-0.5 bg-green-500 mr-2" style={{borderTop: '2px dashed #10b981'}}></div>
                            <span className="text-gray-600">Trend Line</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                <div className="mt-2 text-center text-xs text-gray-600">
                  Y-axis: {(() => {
                    const allTimesForAxis = filteredResults
                      .flatMap(result => 
                        result.result_data?.events?.flatMap(event => 
                          event.draws?.map(draw => parseFloat(draw.finalTime)) || []
                        ) || []
                      )
                      .filter(time => !isNaN(time));
                    if (allTimesForAxis.length === 0) return 'No data';
                    const minTime = Math.min(...allTimesForAxis);
                    const maxTime = Math.max(...allTimesForAxis);
                    return `${minTime.toFixed(3)}s - ${maxTime.toFixed(3)}s`;
                  })()}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {(() => {
                      const allTimesForStats = filteredResults
                        .flatMap(result => 
                          result.result_data?.events?.flatMap(event => 
                            event.draws?.map(draw => parseFloat(draw.finalTime)) || []
                          ) || []
                        )
                        .filter(time => !isNaN(time));
                      return allTimesForStats.length;
                    })()}
                  </div>
                  <div className="text-sm text-gray-600">Total Runs</div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {(() => {
                      const allTimesForStats = filteredResults
                        .flatMap(result => 
                          result.result_data?.events?.flatMap(event => 
                            event.draws?.map(draw => parseFloat(draw.finalTime)) || []
                          ) || []
                        )
                        .filter(time => !isNaN(time));
                      return allTimesForStats.length === 0 ? 'N/A' : Math.min(...allTimesForStats).toFixed(3);
                    })()}s
                  </div>
                  <div className="text-sm text-gray-600">Best Time</div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {(() => {
                      const allTimesForStats = filteredResults
                        .flatMap(result => 
                          result.result_data?.events?.flatMap(event => 
                            event.draws?.map(draw => parseFloat(draw.finalTime)) || []
                          ) || []
                        )
                        .filter(time => !isNaN(time));
                      return allTimesForStats.length === 0 ? 'N/A' : (allTimesForStats.reduce((sum, time) => sum + time, 0) / allTimesForStats.length).toFixed(3);
                    })()}s
                  </div>
                  <div className="text-sm text-gray-600">Average Time</div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {new Set(filteredResults.map(r => r.show_name)).size}
                  </div>
                  <div className="text-sm text-gray-600">Shows</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Results Modal */}
      {showAddResults && (
        <AddResultsModal
          onClose={() => setShowAddResults(false)}
          onAdd={handleAddResults}
          availableResults={availableResults}
          selectedResults={selectedResults}
          setSelectedResults={setSelectedResults}
          onFetchResults={fetchAvailableResults}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              {deletingResultId ? (
                <>Are you sure you want to remove this result from the report? This action cannot be undone.</>
              ) : (
                <>Are you sure you want to delete the report <span className="font-semibold">"{report?.name}"</span>? This action cannot be undone.</>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingResultId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deletingResultId ? confirmRemoveResult : confirmDeleteReport}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {deletingResultId ? 'Remove' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add Results Modal Component
function AddResultsModal({ onClose, onAdd, availableResults, selectedResults, setSelectedResults, onFetchResults }) {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    onFetchResults();
  }, [onFetchResults]);

  const handleSelectResult = (resultId) => {
    setSelectedResults(prev => 
      prev.includes(resultId) 
        ? prev.filter(id => id !== resultId)
        : [...prev, resultId]
    );
  };

  const handleSelectAll = () => {
    if (selectedResults.length === availableResults.length) {
      setSelectedResults([]);
    } else {
      setSelectedResults(availableResults.map(r => r.id));
    }
  };

  const handleAdd = async () => {
    setLoading(true);
    await onAdd();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Add Results to Report</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {availableResults.length} results available that match your filter criteria
            </div>
            {availableResults.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                {selectedResults.length === availableResults.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {availableResults.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No results available to add</p>
                <p className="text-sm mt-2">
                  Either all your results are already in this report, or no results match your filter criteria
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {availableResults.map((result) => (
                  <div
                    key={result.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer ${
                      selectedResults.includes(result.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleSelectResult(result.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {result.show_name || 'Unknown Show'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(result.show_start_date || result.created_at)} • 
                          {result.first_event_name || 'Unknown Event'}
                        </div>
                      </div>
                      <div className="flex items-center">
                        {selectedResults.includes(result.id) ? (
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-300 rounded" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={loading || selectedResults.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Adding...' : `Add ${selectedResults.length} Results`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function for date formatting
function formatDate(dateString) {
  if (!dateString) return 'No date';
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
}
